// Fase 17.3, Planos 05 e 06 — cliente HTTP dos roteiros: lista de peças, modelos, ficha da peça, serviços/células e gravação.
import type {
  AtualizarDadosTecnicosDto,
  DadosTecnicosPecaDto,
  DefinirTintaDaPecaDto,
  FichaPecaRoteiroDto,
  FiltrosPecasRoteiroDto,
  ListaPecasRoteiroDto,
  ModeloRoteiroResumoDto,
  ResultadoGerarOp,
  ResultadoSalvarRoteiroPecaDto,
  SalvarRoteiroPecaDto,
  ServicoComCelulasDto,
  TintaDaPecaDto,
  TratamentoPecaDto,
  SalvarTratamentoPecaDto,
} from '@pieltec/contracts';
import { apiFetch, apiMutate } from '../csrf-fetch';
import { lerErroApi } from '../vendas/erro-api';
import { urlDosFiltros } from './filtros';

const BASE = '/api/v1/roteiros';

export const MENSAGEM_CONFLITO_ROTEIRO = 'Outra pessoa alterou este roteiro. Recarregue a ficha e tente de novo.';

/** Erro de gravação com o status HTTP, para a tela tratar 409 (versão) e 403 (permissão) sem ler texto. */
export class ErroRoteiroApi extends Error {
  constructor(mensagem: string, readonly status: number) {
    super(mensagem);
    this.name = 'ErroRoteiroApi';
  }
}

export function listarPecas(filtros: FiltrosPecasRoteiroDto): Promise<ListaPecasRoteiroDto> {
  const consulta = urlDosFiltros(filtros);
  return apiFetch<ListaPecasRoteiroDto>(`${BASE}/pecas${consulta ? `?${consulta}` : ''}`);
}

export function listarModelos(busca?: string): Promise<ModeloRoteiroResumoDto[]> {
  const params = new URLSearchParams();
  if (busca?.trim()) params.set('busca', busca.trim());
  const consulta = params.toString();
  return apiFetch<ModeloRoteiroResumoDto[]>(`${BASE}/modelos${consulta ? `?${consulta}` : ''}`);
}

export function buscarFicha(materialId: string): Promise<FichaPecaRoteiroDto> {
  return apiFetch<FichaPecaRoteiroDto>(`${BASE}/pecas/${encodeURIComponent(materialId)}`);
}

export function servicosComCelulas(): Promise<ServicoComCelulasDto[]> {
  return apiFetch<ServicoComCelulasDto[]>(`${BASE}/lookups/servicos-celulas`);
}

/** Salva uma nova versão do roteiro da peça (PUT). 409 = outra pessoa salvou antes; 403 = sem roteiros:gerenciar. */
export async function salvarRoteiro(materialId: string, corpo: SalvarRoteiroPecaDto): Promise<ResultadoSalvarRoteiroPecaDto> {
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/roteiro`, { method: 'PUT', body: corpo });
  if (!resposta.ok) {
    if (resposta.status === 409) throw new ErroRoteiroApi(MENSAGEM_CONFLITO_ROTEIRO, 409);
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para editar roteiros.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível salvar o roteiro. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoSalvarRoteiroPecaDto;
}

function mensagemDaFalha(status: number): string | null {
  if (status === 403) return 'Você não tem permissão para esta ação.';
  return status === 404 ? 'Peça não encontrada.' : null;
}

/** Atualiza só os dados técnicos informados (PATCH). Valores vazios viram `null` e limpam o campo. */
export async function atualizarDadosTecnicos(materialId: string, alteracoes: AtualizarDadosTecnicosDto): Promise<DadosTecnicosPecaDto> {
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/dados-tecnicos`, { method: 'PATCH', body: alteracoes });
  if (!resposta.ok) {
    const fixa = mensagemDaFalha(resposta.status);
    if (fixa) throw new ErroRoteiroApi(fixa, resposta.status);
    const erro = await lerErroApi(resposta, 'Não foi possível salvar os dados técnicos. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as DadosTecnicosPecaDto;
}

/** D-08: descarta a cor sugerida pelo CargaMáquina (não altera os dados técnicos). */
export async function ignorarCorSugerida(materialId: string): Promise<void> {
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/cor-sugerida/ignorar`, { method: 'POST' });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, 'Não foi possível ignorar a sugestão. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
}

/** D-03: grava a escolha explícita do PCP; `null` preserva a decisão de voltar à regra por cor. */
export async function definirTintaDaPeca(materialId: string, tintaMaterialId: string | null): Promise<TintaDaPecaDto> {
  const corpo: DefinirTintaDaPecaDto = { tintaMaterialId };
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/tinta`, { method: 'PATCH', body: corpo });
  if (!resposta.ok) {
    if (resposta.status === 403) throw new ErroRoteiroApi('Você não tem permissão para trocar a tinta da peça.', 403);
    const erro = await lerErroApi(resposta, 'Não foi possível trocar a tinta da peça. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as TintaDaPecaDto;
}

/** "Gerar OP" de um lote que aguardava roteiro: a API resolve o roteiro da peça (corpo vazio). */
export async function gerarOpDoLote(loteId: string): Promise<ResultadoGerarOp> {
  const resposta = await apiMutate(`/api/v1/vendas/pedidos/lotes/${encodeURIComponent(loteId)}/gerar-op`, { method: 'POST', body: {} });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, 'Não foi possível gerar a OP. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as ResultadoGerarOp;
}

export async function salvarTratamento(materialId: string, corpo: SalvarTratamentoPecaDto): Promise<TratamentoPecaDto> {
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/tratamentos`, { method: 'POST', body: corpo });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, 'Não foi possível salvar o tratamento. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
  return (await resposta.json()) as TratamentoPecaDto;
}

export async function alterarAtivacaoTratamento(materialId: string, tratamentoId: string, corpo: { ativo: boolean; versaoEsperada: number }): Promise<void> {
  const resposta = await apiMutate(`${BASE}/pecas/${encodeURIComponent(materialId)}/tratamentos/${encodeURIComponent(tratamentoId)}/ativo`, { method: 'PATCH', body: corpo });
  if (!resposta.ok) {
    const erro = await lerErroApi(resposta, 'Não foi possível alterar o tratamento. Tente novamente.');
    throw new ErroRoteiroApi(erro.mensagem, resposta.status);
  }
}
